import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { cn } from '@neo/lib/utils'
import { Avatar, AvatarFallback } from '@neo/primitives/avatar'
import { DateTimePicker } from '@neo/primitives/date-time-picker'
import { Label } from '@neo/primitives/label'
import type { SelectOption } from '@neo/primitives/select'
import { ConvChannels, type LiveAgent, type TaskForm, type Team } from 'v2/types/api/conversations'
import dayjs, { getCurrentTimeInSpecificTimezone } from 'v2/utils/dayjs'
import z from 'zod'
import { TaskAttachment } from './create-task'
import { TaskAssignmentStatusFlag, TaskPriorityFlag, TaskStatusFlag } from './task-components'

export const TASK_DATE_TIME_FORMAT_TIMEZONE = 'ddd MMM DD YYYY HH:mm:ss [GMT]ZZ'

export const TASK_DATE_TIME_RENDER_FORMAT = 'ddd MMM DD YYYY h:mm A'

export const TaskFieldLabelMapping = {
  title: 'title',
  description: 'description',
  due_date: 'due date',
  type: 'type',
  priority: 'priority',
  status: 'status',
  attachments: 'attachments',
  destination_id: 'assignee',
  schedule: 'schedule',
}

export enum TaskActions {
  scheduled = 'scheduled',
  accepted = 'accepted',
  declined = 'declined',
  failed = 'failed',
}

export enum TaskType {
  followUp = 'follow_up',
  internal = 'internal',
  others = 'others',
  // Older task types
  generic = 'generic',
  callback = 'callback',
}

export const TaskTypeLabelMapping = {
  [TaskType.followUp]: 'Follow up',
  [TaskType.internal]: 'Internal',
  [TaskType.others]: 'Others',
  // Older task types
  [TaskType.generic]: 'Others',
  [TaskType.callback]: 'Follow up',
}

export const TaskTypeOptions = [
  { label: TaskTypeLabelMapping[TaskType.followUp], value: TaskType.followUp },
  { label: TaskTypeLabelMapping[TaskType.internal], value: TaskType.internal },
  { label: TaskTypeLabelMapping[TaskType.others], value: TaskType.others },
]

export enum TaskAssigneeType {
  self = 'self',
  agent = 'agent',
  human_agent = 'human_agent',
  team = 'team',
  subFlow = 'task_destination',
}

export const TaskAssigneeTypeOptions = [
  { label: 'Self', value: TaskAssigneeType.self },
  { label: 'Agent', value: TaskAssigneeType.human_agent },
  { label: 'Team', value: TaskAssigneeType.team },
]

export const TaskAssigneeTypeLabelMapping = {
  [TaskAssigneeType.self]: 'You',
  [TaskAssigneeType.agent]: 'Agent',
  [TaskAssigneeType.human_agent]: 'Agent',
  [TaskAssigneeType.team]: 'Team',
}

export const TaskChannelLabelMapping = {
  [ConvChannels.CALL]: 'Call',
  [ConvChannels.WHATSAPP]: 'WhatsApp',
  [ConvChannels.MESSAGE]: 'SMS',
}

export enum TaskPriority {
  low = 'low',
  medium = 'medium',
  high = 'high',
  critical = 'critical',
}

export const TaskPriorityLabelMapping = {
  [TaskPriority.low]: 'Low',
  [TaskPriority.medium]: 'Medium',
  [TaskPriority.high]: 'High',
  [TaskPriority.critical]: 'Critical',
}

export const TaskPriorityOptions = [
  {
    label: <TaskPriorityFlag type={TaskPriority.low} />,
    value: TaskPriority.low,
    searchValue: TaskPriority.low,
  },
  {
    label: <TaskPriorityFlag type={TaskPriority.medium} />,
    value: TaskPriority.medium,
    searchValue: TaskPriority.medium,
  },
  {
    label: <TaskPriorityFlag type={TaskPriority.high} />,
    value: TaskPriority.high,
    searchValue: TaskPriority.high,
  },
  {
    label: <TaskPriorityFlag type={TaskPriority.critical} />,
    value: TaskPriority.critical,
    searchValue: TaskPriority.critical,
  },
]

export enum TaskStatus {
  todo = 'todo',
  inProgress = 'in_progress',
  resolved = 'resolved',
  waitingOnCustomer = 'waiting_on_customer',
}

export const TaskStatusLabelMapping = {
  [TaskStatus.todo]: 'Todo',
  [TaskStatus.inProgress]: 'In progress',
  [TaskStatus.resolved]: 'Resolved',
  [TaskStatus.waitingOnCustomer]: 'Waiting on customer',
}

export const TaskStatusOptions = [
  {
    label: <TaskStatusFlag status={TaskStatus.todo} />,
    value: TaskStatus.todo as string,
    searchValue: TaskStatus.todo,
  },
  {
    label: <TaskStatusFlag status={TaskStatus.inProgress} />,
    value: TaskStatus.inProgress as string,
    searchValue: TaskStatus.inProgress,
  },
  {
    label: <TaskStatusFlag status={TaskStatus.resolved} />,
    value: TaskStatus.resolved as string,
    searchValue: TaskStatus.resolved,
  },
  {
    label: <TaskStatusFlag status={TaskStatus.waitingOnCustomer} />,
    value: TaskStatus.waitingOnCustomer as string,
    searchValue: TaskStatus.waitingOnCustomer,
  },
]

export const TaskChannelOptions = [
  { label: TaskChannelLabelMapping[ConvChannels.CALL], value: ConvChannels.CALL as string },
  {
    label: TaskChannelLabelMapping[ConvChannels.WHATSAPP],
    value: ConvChannels.WHATSAPP as string,
  },
  {
    label: TaskChannelLabelMapping[ConvChannels.MESSAGE],
    value: ConvChannels.MESSAGE as string,
  },
]

export enum TaskAssignmentStatus {
  pending = 'pending',
  accepted = 'accepted',
  declined = 'declined',
  failed = 'failed',
  scheduled = 'scheduled',
}

export const TaskAssignmentStatusLabelMapping = {
  [TaskAssignmentStatus.pending]: 'Pending',
  [TaskAssignmentStatus.accepted]: 'Accepted',
  [TaskAssignmentStatus.declined]: 'Declined',
  [TaskAssignmentStatus.failed]: 'Failed',
  [TaskAssignmentStatus.scheduled]: 'Scheduled',
}

export const TaskAssignmentStatusOptions = [
  {
    label: <TaskAssignmentStatusFlag status={TaskAssignmentStatus.pending} />,
    value: TaskAssignmentStatus.pending,
    searchValue: TaskAssignmentStatus.pending,
  },
  {
    label: <TaskAssignmentStatusFlag status={TaskAssignmentStatus.accepted} />,
    value: TaskAssignmentStatus.accepted,
    searchValue: TaskAssignmentStatus.accepted,
  },
  {
    label: <TaskAssignmentStatusFlag status={TaskAssignmentStatus.declined} />,
    value: TaskAssignmentStatus.declined,
    searchValue: TaskAssignmentStatus.declined,
  },
  {
    label: <TaskAssignmentStatusFlag status={TaskAssignmentStatus.failed} />,
    value: TaskAssignmentStatus.failed,
    searchValue: TaskAssignmentStatus.failed,
  },
  {
    label: <TaskAssignmentStatusFlag status={TaskAssignmentStatus.scheduled} />,
    value: TaskAssignmentStatus.scheduled,
    searchValue: TaskAssignmentStatus.scheduled,
  },
]

export const disableFields = {
  [TaskAssignmentStatus.pending]: [
    'task_type',
    'assignee',
    'assignee_type',
    'dueDate',
    'schedule',
    'timezone',
  ],
}

export const AgentAvatar = ({
  name,
  className = 'w-5 h-5',
}: {
  name: string
  className?: string
}) => {
  const [firstName] = name ? name.split(' ') : ['A']
  const avatarName = `${firstName.charAt(0)}`
  return (
    <Avatar className={cn('uppercase', className)}>
      <AvatarFallback className="bg-bg3 text-primary text-xxs-600">{avatarName}</AvatarFallback>
    </Avatar>
  )
}

export const DestinationIcon = ({ type, name }: { type: TaskAssigneeType; name: string }) => {
  switch (type) {
    case TaskAssigneeType.self:
    case TaskAssigneeType.human_agent: {
      return <AgentAvatar name={name} />
    }
    default:
      return null
  }
}

export const formatTaskDatetime = (datetime: Date, timezone?: string) => {
  if (timezone) {
    const formattedDatetime = dayjs.tz(dayjs(datetime), timezone).toISOString()
    return formattedDatetime
  }

  const formattedDatetime = dayjs
    .tz(dayjs(datetime).format(TASK_DATE_TIME_FORMAT_TIMEZONE), 'UTC')
    .toISOString()
  return formattedDatetime
}

export const getTaskFields = (
  timezones: SelectOption[],
  agents: LiveAgent[],
  teams: Team[],
  agentUuid: string,
): GenericFieldConfig<TaskForm> => {
  const getAssignee = (assigneeType: string) => {
    switch (assigneeType) {
      case TaskAssigneeType.agent:
      case TaskAssigneeType.human_agent:
        return agents.map((agent) => ({
          label: `${agent.account_first_name} ${agent.account_last_name}`,
          value: agent.id,
        }))
      case TaskAssigneeType.team:
        return teams.map((team) => ({ label: team.name, value: team.uuid }))
      default:
        return []
    }
  }

  const getMandatoryLabel = ({ label }: { label: string }) => (
    <Label className="block mb-1.5">
      {label} <span className="text-destructive">*</span>
    </Label>
  )

  return [
    {
      name: 'task_type',
      label: getMandatoryLabel({ label: 'Task type' }),
      componentType: 'select',
      className: 'col-span-3',
      inputProps: {
        options: TaskTypeOptions,
        placeholder: 'Select task type',
        searchable: false,
      },
      zodValidation: z.string().min(1, 'Select a task type'),
      afterOnChange: (values, _, form) => {
        if (values.task_type === TaskType.followUp) {
          form?.setFieldValue('channel', ConvChannels.CALL)
        } else {
          form?.setFieldValue('channel', '' as ConvChannels)
        }
        form?.validateField('channel', 'change')
      },
    },
    {
      name: 'channel',
      label: getMandatoryLabel({ label: 'Channel' }),
      componentType: 'select',
      className: 'col-span-3',
      inputProps: {
        options: TaskChannelOptions,
        placeholder: 'Select channel',
        searchable: false,
      },
      onChangeListenTo: ['task_type'],
      visible: (values) => values.task_type === TaskType.followUp,
      getDynamicProps: (values) => ({
        inputProps: {
          disabled: values.task_type !== TaskType.followUp,
        },
      }),
      zodValidation: (values) =>
        values.task_type === TaskType.followUp
          ? z.string().min(1, 'Select a channel')
          : z.string().nullable(),
    },
    {
      name: 'schedule',
      type: 'custom',
      className: 'col-span-3',
      getDynamicProps: (values) => ({
        inputProps: {
          datesExcluded: { before: getCurrentTimeInSpecificTimezone(values.timezone) },
        },
      }),
      component: ({ form }) => (
        <div className="flex flex-col [&>button]:w-full">
          <Label className="mb-1.5">{getMandatoryLabel({ label: 'Schedule' })}</Label>
          <DateTimePicker
            defaultValue={form.store.state.values.schedule}
            onChange={(value) => {
              form.setFieldValue('schedule', value)
              form.validateField('schedule', 'change')
              if (value) {
                form.setFieldValue('dueDate', value)
                form.validateField('dueDate', 'change')
              }
            }}
            datesExcluded={{
              before: getCurrentTimeInSpecificTimezone(form.store.state.values.timezone),
            }}
          />
        </div>
      ),
      zodValidation: (values) =>
        values.task_type === TaskType.followUp
          ? z
              .date({
                required_error: 'Pick a date & time',
                invalid_type_error: 'Pick a date & time',
              })
              .min(
                getCurrentTimeInSpecificTimezone(values.timezone),
                'Schedule date must be in the future',
              )
          : z.null(),
      visible: (values) => values.task_type === TaskType.followUp,
      onChangeListenTo: ['task_type', 'timezone'],
    },
    {
      name: 'timezone',
      label: getMandatoryLabel({ label: 'Timezone' }),
      componentType: 'select',
      zodValidation: (values) =>
        values.task_type === TaskType.followUp
          ? z.string().min(1, 'Select a timezone')
          : z.string().nullable(),
      className: 'col-span-3',
      inputProps: {
        options: timezones,
        placeholder: 'Select timezone',
      },
      visible: (values) => values.task_type === TaskType.followUp,
      onChangeListenTo: ['task_type'],
    },
    {
      name: 'title',
      label: getMandatoryLabel({ label: 'Title' }),
      componentType: 'input',
      className: 'col-span-6',
      inputProps: {
        type: 'text',
        placeholder: 'Enter task title',
      },
      visible: (values) => values.task_type !== TaskType.followUp,
      onChangeListenTo: ['task_type'],
      zodValidation: z.string().min(1, 'Title is required'),
    },
    {
      name: 'description',
      label: 'Description',
      componentType: 'textarea',
      className: 'col-span-6',
      inputProps: {
        placeholder: 'Enter task description',
      },
      zodValidation: z.string().optional(),
    },
    {
      name: 'assignee_type',
      label: getMandatoryLabel({ label: 'Assign to' }),
      componentType: 'select',
      className: 'col-span-3',
      inputProps: {
        placeholder: 'Select an option',
        searchable: false,
        options: TaskAssigneeTypeOptions,
      },
      onChangeListenTo: ['task_type'],
      afterOnChange: (values, _, form) => {
        if (values.assignee_type === TaskAssigneeType.self) {
          form?.setFieldValue('assignee', agentUuid)
        }
        form?.validateField('assignee', 'change')
      },
      zodValidation: z.string().min(1, 'Select assignee type'),
    },
    {
      name: 'assignee',
      label: '‎',
      componentType: 'select',
      className: 'flex flex-col col-span-3',
      inputProps: {
        placeholder: 'Select an option',
      },
      onChangeListenTo: ['assignee_type'],
      visible: (values) => values.assignee_type !== TaskAssigneeType.self,
      getDynamicProps: (values) => ({
        inputProps: {
          disabled: values.assignee_type && values.assignee_type === TaskAssigneeType.self,
          options: getAssignee(values.assignee_type),
        },
      }),
      zodValidation: (values) =>
        values.assignee_type !== TaskAssigneeType.self
          ? z.string().min(1, 'Select an option')
          : z.any(),
    },
    {
      name: 'priority',
      label: getMandatoryLabel({ label: 'Priority' }),
      componentType: 'select',
      className: 'col-span-3',
      inputProps: {
        options: TaskPriorityOptions,
        placeholder: 'Select priority',
        searchable: false,
      },
      zodValidation: z.string().min(1, 'Select priority'),
    },
    {
      name: 'status',
      label: getMandatoryLabel({ label: 'Status' }),
      componentType: 'select',
      className: 'col-span-3',
      inputProps: {
        options: TaskStatusOptions,
        placeholder: 'Select status',
        searchable: false,
      },
      zodValidation: z.string().min(1, 'Select status'),
    },
    {
      name: 'dueDate',
      type: 'custom',
      className: 'col-span-3',
      getDynamicProps: (values) => ({
        inputProps: {
          datesExcluded: { before: getCurrentTimeInSpecificTimezone(values.timezone) },
          disabled: values.task_type === TaskType.followUp,
        },
      }),
      component: ({ form }) => (
        <div className="flex flex-col [&>button]:w-full">
          {getMandatoryLabel({ label: 'Due date' })}
          <DateTimePicker
            defaultValue={form.store.state.values.dueDate}
            onChange={(value) => {
              form.setFieldValue('dueDate', value as Date)
              form.validateField('dueDate', 'change')
            }}
            disabled={form.store.state.values.task_type === TaskType.followUp}
            datesExcluded={{
              before: getCurrentTimeInSpecificTimezone(form.store.state.values.timezone),
            }}
          />
        </div>
      ),
      onChangeListenTo: ['task_type', 'schedule'],
      visible: (values) => values.task_type !== TaskType.followUp,
      zodValidation: (values) =>
        z
          .date({
            required_error: 'Pick a date & time',
            invalid_type_error: 'Pick a date & time',
          })
          .min(getCurrentTimeInSpecificTimezone(values.timezone), 'Due date must be in the future'),
    },
    {
      name: 'attachments',
      type: 'custom',
      component: ({ form }) => (
        <div className="col-span-6">
          <Label>Attachment</Label>
          <TaskAttachment form={form} />
        </div>
      ),
      zodValidation: z
        .array(
          z.object({
            file_id: z.string(),
            file_name: z.string(),
            file_size: z.number(),
            file_type: z.string(),
            file_url: z.string(),
          }),
        )
        .nullable(),
    },
  ]
}
